import { useState, useEffect, useCallback } from 'react';
import { HorarioResponse } from 'types/horario.types';
import { AñoEscolar } from 'types/usuario.types';
import { horarioService } from 'services/horarioService';
import { catalogoService } from 'services/usuarioService';
import { useAuth } from 'contexts/AuthContext';

// ============================================================
// useHorarioEstudiante – carga el horario del estudiante logueado
// ============================================================

export function useHorarioEstudiante() {
  const { user } = useAuth();

  const [horarios, setHorarios]           = useState<HorarioResponse[]>([]);
  const [añosEscolares, setAñosEscolares] = useState<AñoEscolar[]>([]);
  const [anioSeleccionado, setAnioSeleccionado] = useState<number | null>(null);
  const [loading, setLoading]             = useState(false);
  const [loadingAnios, setLoadingAnios]   = useState(true);
  const [error, setError]                 = useState<string | null>(null);

  // Cargar catálogo de años escolares al montar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const anios = await catalogoService.getAñosEscolares();
        if (cancelled) return;
        setAñosEscolares(anios);
        // Seleccionar el primer año activo por defecto
        if (anios.length > 0) {
          setAnioSeleccionado(anios[0].idAnioEscolar);
        }
      } catch {
        if (!cancelled) setError('No se pudieron cargar los años escolares');
      } finally {
        if (!cancelled) setLoadingAnios(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cargar horarios cuando cambia el año seleccionado
  const cargarHorarios = useCallback(async () => {
    if (!user || !anioSeleccionado) return;
    setLoading(true);
    setError(null);
    try {
      const data = await horarioService.listarPorEstudiante(user.idUsuario, anioSeleccionado);
      setHorarios(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message ?? 'Error al cargar el horario');
      setHorarios([]);
    } finally {
      setLoading(false);
    }
  }, [user, anioSeleccionado]);

  useEffect(() => {
    cargarHorarios();
  }, [cargarHorarios]);

  return {
    horarios,
    añosEscolares,
    anioSeleccionado,
    setAnioSeleccionado,
    loading: loading || loadingAnios,
    error,
    recargar: cargarHorarios,
  };
}
